import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { testCaregiverData, testEarlyAdopterData, testProfessionalData, testSponsorData, testContactData } from '@/utils/formTestData';

interface TestResult {
  formName: string;
  status: 'idle' | 'testing' | 'success' | 'error';
  message?: string;
  error?: string;
  duration?: number;
}

const FormTester = () => {
  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [isRunningAll, setIsRunningAll] = useState(false);

  const updateTestResult = (formName: string, updates: Partial<TestResult>) => {
    setTestResults(prev => {
      const existing = prev.find(r => r.formName === formName);
      if (existing) {
        return prev.map(r => r.formName === formName ? { ...r, ...updates } : r);
      } else {
        return [...prev, { formName, status: 'idle', ...updates }];
      }
    });
  };

  const fillCaregiverForm = async () => {
    const formName = 'Caregiver Application';
    updateTestResult(formName, { status: 'testing' });
    const startTime = Date.now();

    try {
      // Test direct API submission using supabase client
      const { supabase } = await import('@/integrations/supabase/client');
      
      const { data: result, error } = await supabase.functions.invoke('submit-professional-application', {
        body: {
          ...testCaregiverData,
          profession: 'caregiver' as const,
          first_name: testCaregiverData.firstName,
          last_name: testCaregiverData.lastName,
          contact_email: testCaregiverData.email,
        }
      });
      
      if (error) throw error;
      
      toast.success(`${formName}: Form submitted successfully!`);
      updateTestResult(formName, { 
        status: 'success', 
        message: `Form submitted successfully. Check email: ${testCaregiverData.email}`,
        duration: Date.now() - startTime 
      });
    } catch (error: any) {
      console.error('Caregiver form test error:', error);
      updateTestResult(formName, { 
        status: 'error', 
        error: error.message,
        duration: Date.now() - startTime 
      });
    }
  };

  const testEarlyAdopterModal = async () => {
    const formName = 'Early Adopter Modal';
    updateTestResult(formName, { status: 'testing' });
    const startTime = Date.now();

    try {
      // Test direct early adopter signup
      const { supabase } = await import('@/integrations/supabase/client');
      
      const { data: result, error } = await supabase.functions.invoke('early-adopter-signup', {
        body: testEarlyAdopterData
      });
      
      if (error) throw error;
      
      toast.success(`${formName}: Early adopter signup successful!`);
      updateTestResult(formName, { 
        status: 'success', 
        message: `Early adopter signup successful. Check email: ${testEarlyAdopterData.email}`,
        duration: Date.now() - startTime 
      });
    } catch (error: any) {
      console.error('Early adopter modal test error:', error);
      updateTestResult(formName, { 
        status: 'error', 
        error: error.message,
        duration: Date.now() - startTime 
      });
    }
  };

  const testProfessionalForm = async (type: 'ot' | 'caps') => {
    const formName = `Professional - ${type.toUpperCase()}`;
    updateTestResult(formName, { status: 'testing' });
    const startTime = Date.now();

    try {
      // Test direct professional application
      const { supabase } = await import('@/integrations/supabase/client');
      const testData = testProfessionalData[type];
      
      const { data: result, error } = await supabase.functions.invoke('submit-professional-application', {
        body: {
          ...testData,
          first_name: testData.firstName,
          last_name: testData.lastName,
          contact_email: testData.email,
          phone_number: testData.phone,
          license_number: testData.licenseNumber,
          license_state: testData.licenseState,
          years_in_practice: testData.yearsInPractice,
          service_radius: testData.serviceRadius,
        }
      });
      
      if (error) throw error;
      
      toast.success(`${formName}: Professional application successful!`);
      updateTestResult(formName, { 
        status: 'success', 
        message: `Professional application successful. Check email: ${testData.email}`,
        duration: Date.now() - startTime 
      });
    } catch (error: any) {
      console.error('Professional form test error:', error);
      updateTestResult(formName, { 
        status: 'error', 
        error: error.message,
        duration: Date.now() - startTime 
      });
    }
  };

  const testSponsorForm = async () => {
    const formName = 'Sponsor Contact';
    updateTestResult(formName, { status: 'testing' });
    const startTime = Date.now();

    try {
      // Test direct sponsor contact submission
      const { supabase } = await import('@/integrations/supabase/client');
      
      const { data: result, error } = await supabase.functions.invoke('submit-sponsor-inquiry', {
        body: testSponsorData
      });
      
      if (error) throw error;
      
      toast.success(`${formName}: Sponsor inquiry successful!`);
      updateTestResult(formName, { 
        status: 'success', 
        message: `Sponsor inquiry successful. Check email: ${testSponsorData.email}`,
        duration: Date.now() - startTime 
      });
    } catch (error: any) {
      console.error('Sponsor form test error:', error);
      updateTestResult(formName, { 
        status: 'error', 
        error: error.message,
        duration: Date.now() - startTime 
      });
    }
  };

  const testContactForm = async () => {
    const formName = 'Contact Form';
    updateTestResult(formName, { status: 'testing' });
    const startTime = Date.now();

    try {
      // Test contact form submission using the correct edge function
      const { supabase } = await import('@/integrations/supabase/client');
      
      const { data: result, error } = await supabase.functions.invoke('send-contact-email', {
        body: {
          firstName: testContactData.firstName,
          lastName: testContactData.lastName,
          email: testContactData.email,
          phone: testContactData.phone,
          subject: testContactData.subject,
          message: testContactData.message,
          topic: testContactData.topic
        }
      });
      
      if (error) throw error;
      
      toast.success(`${formName}: Contact form submitted successfully!`);
      updateTestResult(formName, { 
        status: 'success', 
        message: `Contact form submitted successfully. Check email: ${testContactData.email}`,
        duration: Date.now() - startTime 
      });
    } catch (error: any) {
      console.error('Contact form test error:', error);
      updateTestResult(formName, { 
        status: 'error', 
        error: error.message,
        duration: Date.now() - startTime 
      });
    }
  };

  const runAllTests = async () => {
    setIsRunningAll(true);
    setTestResults([]);
    
    const tests = [
      () => fillCaregiverForm(),
      () => testContactForm(),
      () => testEarlyAdopterModal(),
      () => testProfessionalForm('ot'),
      () => testProfessionalForm('caps'),
      () => testSponsorForm(),
    ];

    for (const test of tests) {
      try {
        await test();
        await new Promise(resolve => setTimeout(resolve, 1000));
      } catch (error) {
        console.error('Test failed:', error);
      }
    }
    
    setIsRunningAll(false);
    toast.success('All form tests completed');
  };

  const getStatusBadge = (status: TestResult['status']) => {
    const variants = {
      idle: 'secondary' as const,
      testing: 'default' as const, 
      success: 'default' as const,
      error: 'destructive' as const,
    };
    
    const labels = {
      idle: 'Ready',
      testing: 'Testing...',
      success: 'Success',
      error: 'Error',
    };

    return <Badge variant={variants[status]}>{labels[status]}</Badge>;
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Form Testing Dashboard</h2>
        <Button 
          onClick={runAllTests} 
          disabled={isRunningAll}
          size="lg"
          className="bg-gradient-to-r from-blue-600 to-blue-700"
        >
          {isRunningAll ? 'Running Tests...' : 'Test All Forms'}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex justify-between items-center">
              Caregiver Application
              {testResults.find(r => r.formName === 'Caregiver Application') && 
                getStatusBadge(testResults.find(r => r.formName === 'Caregiver Application')!.status)
              }
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Button onClick={fillCaregiverForm} className="w-full mb-2">
              Test Caregiver Form
            </Button>
            <div className="text-xs text-muted-foreground">
              Tests: Individual & Agency paths, all 5 steps, validation, submission
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex justify-between items-center">
              Contact Form
              {testResults.find(r => r.formName === 'Contact Form') && 
                getStatusBadge(testResults.find(r => r.formName === 'Contact Form')!.status)
              }
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Button onClick={testContactForm} className="w-full mb-2">
              Test Contact Form
            </Button>
            <div className="text-xs text-muted-foreground">
              Tests: Form submission, email notifications, data validation
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Professional Forms</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button onClick={() => testProfessionalForm('ot')} size="sm" className="w-full">
              Test OT Network
            </Button>
            <Button onClick={() => testProfessionalForm('caps')} size="sm" className="w-full">
              Test CAPS Network
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Early Adopter Modal</CardTitle>
          </CardHeader>
          <CardContent>
            <Button onClick={testEarlyAdopterModal} className="w-full">
              Test Modal Trigger
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Sponsor Contact</CardTitle>
          </CardHeader>
          <CardContent>
            <Button onClick={testSponsorForm} className="w-full">
              Test Sponsor Form
            </Button>
          </CardContent>
        </Card>
      </div>

      {testResults.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Test Results</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {testResults.map((result, index) => (
                <div key={index} className="flex justify-between items-center p-3 bg-muted rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className="font-medium">{result.formName}</span>
                    {getStatusBadge(result.status)}
                  </div>
                  <div className="text-right text-sm">
                    {result.message && (
                      <div className="text-muted-foreground">{result.message}</div>
                    )}
                    {result.error && (
                      <div className="text-red-500">{result.error}</div>
                    )}
                    {result.duration && (
                      <div className="text-xs text-muted-foreground">
                        {result.duration}ms
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Test Data Preview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <h4 className="font-semibold mb-2">Contact Data Sample:</h4>
              <pre className="text-xs bg-muted p-2 rounded overflow-auto">
                {JSON.stringify({
                  name: `${testContactData.firstName} ${testContactData.lastName}`,
                  email: testContactData.email,
                  phone: testContactData.phone,
                  subject: testContactData.subject,
                  topic: testContactData.topic
                }, null, 2)}
              </pre>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Professional Data Sample:</h4>
              <pre className="text-xs bg-muted p-2 rounded overflow-auto">
                {JSON.stringify({
                  name: `${testProfessionalData.ot.firstName} ${testProfessionalData.ot.lastName}`,
                  profession: testProfessionalData.ot.profession,
                  email: testProfessionalData.ot.email,
                  licenseState: testProfessionalData.ot.licenseState
                }, null, 2)}
              </pre>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FormTester;